import { useState } from 'react';
import { 
  Search, Phone, User, 
  // Calendar, AlertTriangle, 
  Download, Eye, 
  ArrowLeft,
  Loader2,
  // Languages 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
// import {  Call } from '../../types';
import { useGetUsers } from '../../services/query/user/user';
import { UserPatient } from '../../services/api/user/userApi';
import { formatDate, formatTimeOnly } from '../../lib/utils/date-time';
import { useGetUserCallsById } from '../../services/query/calls/callsQueries';
import { UserCall } from '../../services/api/calls/callsApi';
import { useGetConversationDetails } from '../../services/query/calls/conversations';
import { ConversationDetails } from '../../services/api/calls/conversationsApi';
import { useDebounce } from '../../hooks/useDebounce';

export default function PatientList() {
  const { dispatch } = useAppContext();
  const navigate = useNavigate();
  
  const [debouncedSearchTerm, setSearchTerm] = useDebounce<string | undefined>(undefined, 300);
  // const [filterLanguage, setFilterLanguage] = useState<'all' | 'en' | 'es'>('all');
  const [selectedPatient, setSelectedPatient] = useState<UserPatient | null>(null);
  const [selectedCall, setSelectedCall] = useState<UserCall | null>(null);

  const nameParam = typeof debouncedSearchTerm === 'string' && debouncedSearchTerm.trim() !== ''
    ? debouncedSearchTerm.trim()
    : undefined;

  const { 
    data: users, 
    isLoading: isUsersLoading 
  } = useGetUsers({ 
    name: nameParam
  });
  const {
     data: userCalls, 
     isLoading: isUserCallsLoading 
    } = useGetUserCallsById(selectedPatient?.id);
  const { 
    data: callConversation, 
    isLoading: isCallConversationLoading 
  } = useGetConversationDetails(selectedCall?.id);

  // console.log(callConversation, 'callConversation');
  
  const downloadCallTranscript = (callDetails: ConversationDetails) => {
    const transcript = callDetails.messages.map(msg => 
      `[${new Date(msg.created_at).toLocaleTimeString()}] ${msg.speaker.toUpperCase()}: ${msg.text}`
    ).join('\n');
    
    const blob = new Blob([transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-transcript-${callDetails.user_name}-${callDetails.user_id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // const getUrgencyColor = (level: Call['urgencyLevel']) => {
  //   switch (level) {
  //     case 'emergency': return 'text-red-600 bg-red-100';
  //     case 'high': return 'text-orange-600 bg-orange-100';
  //     case 'medium': return 'text-yellow-600 bg-yellow-100';
  //     default: return 'text-green-600 bg-green-100';
  //   }
  // };

  // Call conversation view
  if (selectedCall) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSelectedCall(null)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Call Conversation</h2>
              <p className="text-gray-600">{selectedCall.user_name} - {formatDate(selectedCall.start_time)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* {selectedCall.messages.some(msg => msg.language === 'es') && (
              <div className="flex items-center space-x-1 px-2 py-1 bg-blue-100 rounded-full">
                <Languages className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-blue-800 font-medium">Spanish</span>
              </div>
            )} */}
            <button
              onClick={() => {
					if (!callConversation) return;
					downloadCallTranscript(callConversation as ConversationDetails);
				}}
				disabled={isCallConversationLoading || !callConversation}
              className={`flex items-center space-x-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 ${
					isCallConversationLoading || !callConversation ? 'opacity-50 cursor-not-allowed' : ''
				}`}
            >
              <Download className="w-4 h-4" />
              <span>Download Transcript</span>
            </button>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 h-96 overflow-y-auto">
			{isCallConversationLoading ? (
				<div className="flex items-center justify-center py-12">
					<Loader2 className="w-8 h-8 animate-spin text-green-600" />
					<span className="ml-3 text-gray-600">Loading conversation...</span>
				</div>
			) : (
				callConversation?.messages.map((message) => (
					<div
						key={message.id}
						className={`mb-4 flex ${message.speaker === 'assistant' ? 'justify-start' : 'justify-end'}`}
					>
						<div
							className={`max-w-xs px-4 py-2 rounded-lg ${
								message.speaker === 'assistant'
									? 'bg-white text-gray-900 border border-gray-200'
									: 'bg-green-500 text-white'
							}`}
						>
							<p className="text-sm">{message.text}</p>
							<div className="flex items-center justify-between mt-2">
								<span className="text-xs opacity-70">{new Date(message.created_at).toLocaleTimeString()}</span>
							</div>
						</div>
					</div>
				))
			)}
        </div>

        <div className="mt-4 p-4 bg-gray-100 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Duration:</span>
              <p className="text-gray-900">{selectedCall.duration_seconds ? formatDuration(selectedCall.duration_seconds) : 'N/A'}</p>
            </div>
            <div>
              {/* <span className="font-medium text-gray-700">Purpose:</span>
              <p className="text-gray-900">{selectedCall.purpose}</p> */}
            </div>
            {/* <div>
              <span className="font-medium text-gray-700">Urgency:</span>
              <span className={`px-2 py-1 text-xs rounded-full ${getUrgencyColor(selectedCall.urgencyLevel)}`}>
                {selectedCall.urgencyLevel}
              </span>
            </div> */}
            {/* <div>
              <span className="font-medium text-gray-700">Messages:</span>
              <p className="text-gray-900">{selectedCall.messages.length}</p>
            </div> */}
          </div>
        </div>
      </div>
    );
  }

  // Patient call records view
  if (selectedPatient) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSelectedPatient(null)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Call Records</h2>
              <p className="text-gray-600">{selectedPatient.first_name} {' '} {selectedPatient.last_name} - {selectedPatient.total_calls ? selectedPatient.total_calls : '0'} calls</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* <span className={`px-2 py-1 text-xs rounded-full ${
              selectedPatient.language === 'es' ? 'bg-emerald-100 text-emerald-800' : 'bg-green-100 text-green-800'
            }`}>
              {selectedPatient.language === 'es' ? 'Spanish' : 'English'}
            </span> */}

            <button
              onClick={() => navigate(`/patients/profile/${selectedPatient?.id}`)}
              className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
            >
              View Profile
            </button>
          </div>
        </div>

        {isUserCallsLoading ? (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="w-8 h-8 animate-spin text-green-600" />
				<span className="ml-3 text-gray-600">Loading call records...</span>
			</div>
		) : !userCalls?.data || userCalls.data.length === 0 ? (
          <div className="text-center py-12">
            <Phone className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No call records found</p>
            <p className="text-sm text-gray-400">This patient hasn't made any calls yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {userCalls?.data.map((call) => (
              <div
                key={call.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedCall(call)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-3 h-3 rounded-full ${
                      call.status === 'connected' ? 'bg-green-500' :
                      call.status === 'ended' ? 'bg-gray-500' : 'bg-yellow-500'
                    }`}></div>
                    
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-gray-900">
                          {formatDate(call.start_time)} at {formatTimeOnly(call.start_time)}
                        </h3>
                        {/* {call.messages.some(msg => msg.language === 'es') && (
                          <Languages className="w-4 h-4 text-blue-500" />
                        )} */}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                        {/* <span>{call.type} call</span> */}
                        {/* <span>•</span> */}
                        {/* <span>{call.purpose}</span> */}
                        {call.duration_seconds && (
                          <>
                            <span>Duration:</span>
                            <span>{formatDuration(call.duration_seconds)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* <span className={`px-2 py-1 text-xs rounded-full ${getUrgencyColor(call.urgencyLevel)}`}>
                      {call.urgencyLevel}
                    </span> */}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCall(call);
                      }}
                      className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="View Conversation"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    
                    {/* <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // downloadCallTranscript(call);
                      }}
                      className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title="Download Transcript"
                    >
                      <Download className="w-4 h-4" />
                    </button> */}
                  </div>
                </div>

                {call.summary && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Summary:</span> {call.summary}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Main patient directory view
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Patient Directory</h2>
			<span className="text-sm text-gray-500">{isUsersLoading ? 'Loading...' : `${users?.data.length ?? 0} patients`}</span>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search patients by name or phone..."
            value={debouncedSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        
        {/* <select
          value={filterLanguage}
          onChange={(e) => setFilterLanguage(e.target.value as 'all' | 'en' | 'es')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value="all">All Languages</option>
          <option value="en">English</option>
          <option value="es">Spanish</option>
        </select> */}
      </div>

      {/* Patient List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
			{isUsersLoading ? (
				<div className="flex items-center justify-center py-12">
					<Loader2 className="w-8 h-8 animate-spin text-green-600" />
					<span className="ml-3 text-gray-600">Loading patients...</span>
				</div>
			) : (
				users && users?.data?.length > 0 && users.data.map((patient) => {
          return (
            <div
              key={patient.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              onClick={() => {
                setSelectedPatient(patient);
                dispatch({ type: 'SET_SELECTED_PATIENT', payload: patient });
              }}
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-green-600" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900">
                      {patient.first_name} {patient.last_name}
                    </h3>
                    {/* {patient.allergies.length > 0 && (
                      <AlertTriangle className="w-4 h-4 text-yellow-500"  />
                    )} */}
                    {/* <span className={`px-2 py-1 text-xs rounded-full ${
                      patient.language === 'es' ? 'bg-emerald-100 text-emerald-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {patient.language === 'es' ? 'ES' : 'EN'}
                    </span> */}
                  </div>
                  <p className="text-sm text-gray-600">{patient.phone}</p>
                  <p className="text-xs text-gray-500">
                    Last call: {patient.last_call_date ? formatDate(patient.last_call_date) : '-'} • {patient.total_calls ? patient.total_calls : '0'} calls
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {/* {patient.next_appointment && (
                  <div className="text-right mr-4">
                    <p className="text-xs text-gray-500">Next appointment</p>
                    <p className="text-sm font-medium text-gray-900 flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(patient.next_appointment)}
                    </p>
                  </div>
                )} */}
                
                <span className="text-sm text-gray-500">
                  {patient.total_calls} calls
                </span>
              </div>
            </div>
          );
				})
			)}
      </div>
    </div>
  );
}